/**
 * Executive Summary Card — above-the-fold intelligence summary
 *
 * Shows: recommendation, 3 key metrics with trend arrows, strength/risk pills.
 * Positioned at the very top of the Overview tab.
 */

import type { ExecutiveSummary, TrendDirection } from '../../../types';

interface ExecutiveSummaryCardProps {
  summary: ExecutiveSummary;
}

function TrendArrow({ trend, className = '' }: { trend: TrendDirection; className?: string }) {
  const config: Record<TrendDirection, { arrow: string; color: string }> = {
    up: { arrow: '\u2191', color: 'text-green-600' },
    down: { arrow: '\u2193', color: 'text-red-600' },
    stable: { arrow: '\u2192', color: 'text-gray-400' },
  };

  const { arrow, color } = config[trend] || config.stable;

  return (
    <span className={`font-semibold ${color} ${className}`} aria-label={`Trend: ${trend}`}>
      {arrow}
    </span>
  );
}

export function ExecutiveSummaryCard({ summary }: ExecutiveSummaryCardProps) {
  return (
    <div
      className="rounded-lg border border-primary-200 bg-gradient-to-r from-primary-50/50 to-white p-6 shadow-sm"
      data-testid="executive-summary-card"
    >
      {/* Recommendation */}
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex items-center rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-semibold text-primary-800">
            Executive Summary
          </span>
        </div>
        <p className="text-base text-gray-900 font-medium leading-relaxed">
          {summary.recommendation}
        </p>
      </div>

      {/* Key Metrics */}
      {summary.key_metrics.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
          {summary.key_metrics.map((metric) => (
            <div
              key={metric.label}
              className="rounded-md bg-white border border-gray-100 p-3"
              data-testid="key-metric"
            >
              <div className="text-xs text-gray-500 mb-1">{metric.label}</div>
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold text-gray-900">{metric.value}</span>
                <TrendArrow trend={metric.trend} />
              </div>
              {metric.trend_detail && (
                <div className="text-xs text-gray-400 mt-0.5">{metric.trend_detail}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Strengths and Risks */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {summary.strengths.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Strengths
            </div>
            <div className="flex flex-wrap gap-1.5">
              {summary.strengths.map((strength, i) => (
                <span
                  key={i}
                  className="inline-flex items-center rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-800 border border-green-100"
                  data-testid="strength-pill"
                >
                  {strength}
                </span>
              ))}
            </div>
          </div>
        )}

        {summary.risks.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Risks
            </div>
            <div className="flex flex-wrap gap-1.5">
              {summary.risks.map((risk, i) => (
                <span
                  key={i}
                  className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-1 text-xs font-medium text-red-800 border border-red-100"
                  data-testid="risk-pill"
                >
                  {risk}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
